import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { CardBlock } from '@lighthouse/block/src/blocks'
import type { ActorEvent, CardBlockAbstract, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { fieldConvertValue, type RichTextEditorProps, useApplicationContext } from '@lighthouse/shared'
import equal from 'fast-deep-equal'
import React, { memo, useCallback, useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface CardBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: CardBlockAbstract
}

const CardBlockController: React.FC<CardBlockControllerProps> = ({ blockData, ...rest }) => {
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const { config, id } = blockData
    const { list } = config
    const { handleActionListTrigger } = useActionTrigger(id)
    const { record } = useCustomViewBlockContext()
    const { scope } = useContainerBlockContext()
    const allRichtext = useMemo(
        () =>
            list.reduce<RichTextContentProtocol[]>((p, c) => {
                if (c.heading) {
                    p.push(c.heading)
                }

                if (c.introduction) {
                    p.push(c.introduction)
                }
                return p
            }, []),
        [list]
    )

    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable = useParseVariableWithSubscription(allRichtext)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value, {
                viewRecord: record,
                scope
            })
            if (!field) {
                if (value.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(value)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [departmentOptions, getPageDataQueryVariableLabel, parseVariable, personOptions, record, roleOptions, scope]
    )
    const cardConfig: RichTextEditorProps['config'] = useMemo(
        () => ({
            mode: 'text-style',
            variable: {
                renderLabel: v => parseVariable2String(v.attrs.value)
            }
        }),
        [parseVariable2String]
    )

    const handleClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger, record]
    )

    return <CardBlock blockData={blockData} config={cardConfig} onClickEvent={handleClickEvent} {...rest} />
}

export default memo(CardBlockController, equal)
